/* Aether — add a suggested bottle in place (R150, staging v15, 27 Oct run 60).
   Used by snippets/aether-cart-beside ("Another from Chapter One") in the cart drawer and
   on the cart page. Each size is a submit button in a plain form that posts to /cart/add,
   so without this script the shopper lands on the cart page with the bottle added.
   With it, the add goes through the theme's own route and cart event, the same way the
   product page adds: CartLinesUpdateEvent with a deferred promise that resolves with the
   rendered cart sections, so cart-items-component morphs the drawer or page, the badge
   updates and the next suggestion appears. Before enhancement starts, the native form
   remains the fallback. Once an add is sent, it is never replayed: an uncertain add or
   failed refresh opens the bag with a GET so the shopper can check it before adding again.
   One listener on the document keeps working after the drawer re-renders.
   Run 62 (R152): also serves the product page pairing panel (snippets/aether-pair). */

import { CartLinesUpdateEvent } from '@shopify/events';

const FLAG = '__aetherCartAdd';

if (!window[FLAG]) {
  window[FLAG] = true;

  document.addEventListener('submit', async (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-acb-add]')) return;

    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    if (!submitter || form.dataset.busy === 'true' || form.dataset.review === 'true') {
      if (form.dataset.busy === 'true' || form.dataset.review === 'true') event.preventDefault();
      return;
    }

    const routes = window.Theme?.routes;
    if (!routes?.cart_add_url || !routes?.cart_url || typeof fetch !== 'function' ||
        typeof CartLinesUpdateEvent?.createPromise !== 'function' ||
        typeof CartLinesUpdateEvent?.createCartFromAjaxResponse !== 'function') return;

    // Leave the native form alone if enhancement cannot be prepared before any request.
    let formData;
    let deferred;
    let id;
    try {
      formData = new FormData(form, submitter);
      id = String(formData.get('id') || '');
      if (!id) return;
      const sections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (sections.length) {
        formData.set('sections', [...new Set(sections)].join(','));
        formData.set('sections_url', window.location.pathname);
      }
      deferred = CartLinesUpdateEvent.createPromise();
    } catch (error) {
      return;
    }

    event.preventDefault();

    const status = form.querySelector('.acb__status');
    const name = submitter.querySelector('.acb__size-name');
    const original = name ? name.textContent : '';
    form.dataset.busy = 'true';
    submitter.setAttribute('aria-busy', 'true');
    submitter.dataset.state = 'adding';
    if (name) name.textContent = 'Adding';
    if (status) status.textContent = 'Adding to your bag.';

    let confirmed = false;
    const reviewBag = (message) => {
      // A failed response is not proof that the add did not happen. Never post again.
      form.dataset.review = 'true';
      form.dataset.busy = 'false';
      submitter.removeAttribute('aria-busy');
      delete submitter.dataset.state;
      if (name) name.textContent = original;
      form.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = true; });
      const note = form.querySelector('.acb__error');
      if (note) {
        note.textContent = `${message} `;
        const link = document.createElement('a');
        link.href = routes.cart_url;
        link.textContent = 'View your bag';
        note.appendChild(link);
        note.hidden = false;
      }
      if (status) status.textContent = message;
      deferred.reject(new Error(message));
      // Navigation is a GET, including when the add response itself was inconclusive.
      // If navigation is unavailable, the link and locked controls remain usable feedback.
      try { window.location.assign(routes.cart_url); } catch (error) { /* Keep the review link. */ }
    };

    try {
      form.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: id, quantity: 1 }],
          promise: deferred.promise,
        })
      );
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const added = await response.json();
      if (response.status === 422 && typeof added?.description === 'string') {
        // Inventory errors can still change quantities; check the bag before another add.
        return reviewBag(`${added.description} Check your bag before adding again.`);
      }
      if (!response.ok || !added || typeof added !== 'object' || added.status || added.errors) {
        throw new Error('Unconfirmed add');
      }
      confirmed = true;

      const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'aether-cart-beside',
          sections: added.sections,
          didError: false,
        },
      });
      form.dataset.busy = 'false';
      form.dataset.review = 'true';
      submitter.removeAttribute('aria-busy');
      submitter.dataset.state = 'added';
      if (name) name.textContent = 'Added';
      if (status) status.textContent = 'Added to your bag.';
      form.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = true; });

      // Run 62 (R152): outside the cart (the product page pairing panel) the form stays put,
      // so it shows its own "View your bag" link and focus stays with the form.
      const done = form.querySelector('.acb__done');
      if (done) done.hidden = false;
      const inCart = Boolean(form.closest('.acb'));

      // The pressed button leaves with the redraw; keep focus inside the cart.
      const scope = form.closest('dialog') || document;
      window.setTimeout(() => {
        if (submitter.isConnected && document.activeElement !== document.body) return;
        const target = inCart
          ? (scope.querySelector('.acb__head') || scope.querySelector('#cart-drawer-heading'))
          : (done?.querySelector('a') || form.querySelector('.acb__add-label'));
        if (!target) return;
        if (!target.matches('a[href], button') && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }, 450);
    } catch (error) {
      reviewBag(confirmed
        ? 'Your bag was updated, but could not be refreshed. Check your bag before adding again.'
        : 'We could not confirm the add. Check your bag before adding again.');
    }
  });
}
